/**
 * Root layout component for the Remix application (T169, T170).
 *
 * This module provides the document structure, theme management,
 * React Query setup, Sentry error tracking, accessibility features,
 * and error handling for the entire application.
 */

import type { LinksFunction, MetaFunction } from '@remix-run/node';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
  useLocation,
} from '@remix-run/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@yak/ui';
import { useState, useEffect, type ReactNode } from 'react';

import { AppToastProvider } from '~/components/ToastProvider';
import '~/styles/tailwind.css';
import { SkipLink, announceToScreenReader } from '~/lib/a11y';
import { trackPageView, setAnalyticsUser } from '~/lib/analytics';
import { captureException, setUser as setSentryUser } from '~/lib/sentry.client';

/**
 * Document meta tags for SEO and PWA support.
 */
export const meta: MetaFunction = () => {
  return [
    { title: 'Yak Interactive Dashboard' },
    {
      name: 'description',
      content: 'Yak Interactive Dashboard - Manage your trivia content and analytics',
    },
    { name: 'viewport', content: 'width=device-width,initial-scale=1' },
    { charSet: 'utf-8' },
  ];
};

/**
 * Stylesheet and font links.
 */
export const links: LinksFunction = () => {
  return [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap',
    },
    { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
  ];
};

/**
 * Create a singleton QueryClient for the application.
 */
function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: process.env.NODE_ENV === 'production',
        retry: 1,
        refetchOnReconnect: false,
      },
      mutations: {
        retry: 1,
      },
    },
  });
}

/**
 * Skip link component for keyboard accessibility (T170).
 * Allows keyboard users to skip navigation and jump to main content.
 */
function SkipToContent() {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target = document.getElementById('main-content');
    if (target) {
      // Make the target focusable if it isn't already
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus();
      announceToScreenReader('Skipped to main content');
    }
  };

  return (
    <a
      href="#main-content"
      onClick={handleClick}
      className="skip-link absolute left-[-9999px] z-[9999] bg-background px-4 py-2 text-foreground underline focus:left-0 focus:top-0 focus:outline-2 focus:outline-offset-2 focus:outline-ring"
    >
      Skip to main content
    </a>
  );
}

/**
 * Document component with HTML structure.
 * Forces light theme.
 * Includes skip link for accessibility.
 */
function Document({ children }: { children: ReactNode }) {
  return (
    <html lang="en" className="light" suppressHydrationWarning>
      <head>
        <Meta />
        <Links />
        {/* Skip link styles */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .skip-link:not(:focus) {
                position: absolute;
                width: 1px;
                height: 1px;
                padding: 0;
                margin: -1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
                white-space: nowrap;
                border: 0;
              }
            `,
          }}
        />
      </head>
      <body className="min-h-screen bg-background font-sans antialiased" suppressHydrationWarning>
        {/* Skip link for keyboard accessibility */}
        <SkipToContent />
        <AppToastProvider>
          {children}
        </AppToastProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Root layout with all providers.
 * ReactQueryDevtools is rendered inside Document for proper hydration.
 */
export function Layout({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => createQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="yi-ui-theme">
        <Document>{children}</Document>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

/**
 * Main application component.
 * Tracks page views and manages focus on navigation.
 */
export default function App() {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return <Outlet />;
}

/**
 * Error boundary for catching and displaying errors.
 * Reports errors to Sentry for monitoring and debugging.
 */
export function ErrorBoundary() {
  const error = useRouteError();

  // Report error to Sentry
  useEffect(() => {
    if (error) {
      captureException(error, {
        tags: { boundary: 'root' },
        extra: {
          isRouteErrorResponse: isRouteErrorResponse(error),
        },
      });
    }
  }, [error]);

  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="mx-auto max-w-md text-center" role="alert">
          <h1 className="mb-4 text-6xl font-bold text-destructive">
            {error.status}
          </h1>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            {error.statusText}
          </h2>
          <p className="mb-8 text-muted-foreground">
            {error.data?.message || 'An unexpected error occurred.'}
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  const errorMessage =
    error instanceof Error ? error.message : 'An unexpected error occurred.';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="mx-auto max-w-md text-center" role="alert">
        <h1 className="mb-4 text-4xl font-bold text-destructive">
          Something went wrong
        </h1>
        <p className="mb-8 text-muted-foreground">{errorMessage}</p>
        <a
          href="/"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          Return Home
        </a>
      </div>
    </div>
  );
}

/**
 * Helper functions exported for use in child routes.
 * These allow setting user context for Sentry and Analytics.
 */
export { setSentryUser, setAnalyticsUser };
